import { useCallback, useEffect, useRef, useState } from 'react';
import postService from '../services/postService';
import { validateVideoSelection } from '../utils/postMedia';

/**
 * Feed-Videos (Marcel 13.10.2026): Video-Entwürfe in Composer/Editor – Dauer im Browser lesen,
 * sofort hochladen (Fortschritt), danach Status bis ready/failed abfragen. EINE Stelle für beide Dialoge.
 */
export type VideoDraftStatus = 'checking' | 'uploading' | 'processing' | 'ready' | 'failed';

export interface VideoDraft {
  key: string;
  videoId: string | null;
  poster: string | null;
  duration: number | null;
  status: VideoDraftStatus;
  progress: number;
  /** Objekt-URL der lokalen Datei für die Vorschau */
  previewUrl: string;
  error?: string | null;
}

/** Dauer aus den Browser-Metadaten (null, wenn nicht lesbar – das Backend prüft dann). */
export function readVideoDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const v = document.createElement('video');
      v.preload = 'metadata';
      const done = (d: number | null) => { try { URL.revokeObjectURL(url); } catch { /* ignore */ } resolve(d); };
      v.onloadedmetadata = () => done(Number.isFinite(v.duration) ? v.duration : null);
      v.onerror = () => done(null);
      v.src = url;
    } catch {
      resolve(null);
    }
  });
}

export type VideoSelectionError = 'empty' | 'too-large' | 'too-long' | 'upload-failed';

export function useVideoDrafts(onError?: (code: VideoSelectionError, message?: string) => void) {
  const [drafts, setDrafts] = useState<VideoDraft[]>([]);
  const draftsRef = useRef<VideoDraft[]>([]);
  draftsRef.current = drafts;

  const patch = useCallback((key: string, p: Partial<VideoDraft>) => {
    setDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, ...p } : d)));
  }, []);

  /**
   * Datei sofort als Kachel anlegen (Marcel 13.10.2026: Rückmeldung in unter 100 ms), dann im Hintergrund
   * Dauer lesen → prüfen → hochladen. Bei Ablehnung verschwindet die Kachel wieder und `onError` meldet den Grund.
   * Liefert den Draft-Key sofort; `onRejected(key)` sagt dem Aufrufer, dass er den Platz wieder freigeben soll.
   */
  const addFile = useCallback((file: File, onRejected?: (key: string) => void): string => {
    const key = `video-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const previewUrl = URL.createObjectURL(file);
    setDrafts((prev) => [...prev, { key, videoId: null, poster: null, duration: null, status: 'checking', progress: 0, previewUrl }]);
    void (async () => {
      const duration = await readVideoDuration(file);
      const check = validateVideoSelection({ size: file.size, duration });
      if (!check.valid) {
        setDrafts((prev) => prev.filter((d) => d.key !== key));
        onError?.(check.error as VideoSelectionError);
        onRejected?.(key);
        return;
      }
      patch(key, { duration, status: 'uploading', progress: 0 });
      try {
        const up = await postService.uploadVideo(file, (percent) => patch(key, { progress: percent }));
        patch(key, { videoId: up.videoId, poster: up.poster, duration: up.duration ?? duration, status: up.status === 'ready' ? 'ready' : up.status === 'failed' ? 'failed' : 'processing', progress: 100 });
      } catch (err: any) {
        setDrafts((prev) => prev.filter((d) => d.key !== key));
        const msg = err?.response?.data?.error ?? (typeof err?.response?.data === 'string' ? err.response.data : null) ?? err?.message ?? null;
        onError?.('upload-failed', msg);
        onRejected?.(key);
      }
    })();
    return key;
  }, [onError, patch]);

  const remove = useCallback((key: string) => {
    setDrafts((prev) => {
      const d = prev.find((x) => x.key === key);
      if (d) { try { URL.revokeObjectURL(d.previewUrl); } catch { /* ignore */ } }
      return prev.filter((x) => x.key !== key);
    });
  }, []);

  // Status-Polling für „processing“ (alle 2 s)
  useEffect(() => {
    if (!drafts.some((d) => d.status === 'processing' && d.videoId)) return;
    const timer = setInterval(async () => {
      for (const d of draftsRef.current) {
        if (d.status !== 'processing' || !d.videoId) continue;
        const st = await postService.getVideoStatus(d.videoId);
        if (st && st.status !== 'processing') patch(d.key, { status: st.status, poster: st.poster ?? d.poster, duration: st.duration ?? d.duration, error: st.error ?? null });
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [drafts, patch]);

  const uploading = drafts.some((d) => d.status === 'checking' || d.status === 'uploading');
  return { drafts, addFile, remove, uploading, patch };
}

export default useVideoDrafts;
